import { useCallback, useEffect, useRef, useState } from "react";
import { encryptStorage } from "../../constant";
import BreadCrumbs from "../BreadCrumbs/BreadCrumbs";
import { UpcomingSession } from "./UpcomingSessions";
import { SessionHistory } from "./SessionHistory";
import { ManageSession } from "./ManageSession";
import { SessionForm } from "./SessionForm";
import { Toaster } from "./Toast";
import { GetSessions } from "../../service/SessionAPI";
import { getCertificationCount } from "../../service/certificationAPI";
import { getS3 } from "../../utils/Download";


export const SessionOverview = () => {
    const [upcomingSessionData, setUpcomingSessionData] = useState({
        sessions: [],
        fbp_allocation: 0,
        total_count: 0,
    });
    const [historySessionData, setHistorySessionData] = useState({
        sessions: [],
        fbp_allocation: 0,
        total_count: 0,
    });
    const [manageSessionData, setManageSessionData] = useState({
        sessions: [],
        fbp_allocation: 0,
        total_count: 0,
    });
    const [activeTab, setActiveTab] = useState('upcomingSessions');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterParams, setFilterParams] = useState({ startDate: '', endDate: '', status: '' });
    const [tempFilterParams, setTempFilterParams] = useState({ startDate: '', endDate: '', status: '' });
    const [sort, setSort] = useState('session_name');
    const [sortDirection, setSortDirection] = useState('asc');
    const [limit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [fbpAllocation, setFbpAllocation] = useState(0);
    const [grade, setGrade] = useState(''); // State for grade
    const [s3Link, sets3Link] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const breadCrumnData = [
        { title: 'Dashboard', path: '/overview', isActive: true },
        { title: 'How Do I', path: '/overview', isActive: true },
        { title: 'Soft Skills', path: '/session', isActive: false },
    ];

    const groupId = encryptStorage.getItem("groupid");
    const empId = encryptStorage.getItem("employeeId");

    const tabRefs = useRef({
        upcomingSessions: null,
        sessionHistory: null,
        manageSessions: null,
    });

    useEffect(() => {
        fetchCertificationCount();
    }, []);

    const fetchSessions = useCallback(async (
        tab,
        filter = filterParams,
        search = searchTerm,
        currentSort = sort,
        currentSortDirection = sortDirection,
        currentOffset = offset,
        currentLimit = limit,
        append = false
    ) => {
        const validTabs = ['upcoming', 'history', 'manage'];
        if (!validTabs.includes(tab)) {
            console.error(`Invalid tab value: ${tab}. Expected one of: ${validTabs.join(', ')}`);
            setError(prev => ({ ...prev, [tab]: `Invalid tab value: ${tab}` }));
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const sortParam = currentSortDirection === 'desc' ? `-${currentSort}` : currentSort;
            const response = tab === 'manage'
                ? await GetSessions(empId, filter, search, '', sortParam, currentLimit, currentOffset)
                : await GetSessions(empId, filter, search, grade, sortParam, currentLimit, currentOffset);
            if (response.data.code === 200) {
                const data = response.data.data;
                const sessions = Array.isArray(data.sessions) ? data.sessions : [];
                const setData = {
                    upcoming: setUpcomingSessionData,
                    history: setHistorySessionData,
                    manage: setManageSessionData,
                }[tab];
                if (append) {
                    setData(prev => ({
                        ...prev,
                        sessions: [...prev.sessions, ...sessions],
                        total_count: data.total_count || prev.total_count,
                    }));
                } else {
                    setData({
                        sessions,
                        fbp_allocation: data.fbp_allocation || 0,
                        total_count: data.total_count || 0,
                    });
                }
                if (tab === 'history') {
                    setTotalCount(data.total_count || 0);
                }
            } else {
                setError("Failed to fetch sessions");
                const setData = {
                    upcoming: setUpcomingSessionData,
                    history: setHistorySessionData,
                    manage: setManageSessionData,
                }[tab];
                setData({ sessions: [], fbp_allocation: 0, total_count: 0 });
            }
        } catch (error) {
            console.error('Error fetching sessions:', error);
            setError("Error fetching sessions");
            const setData = {
                upcoming: setUpcomingSessionData,
                history: setHistorySessionData,
                manage: setManageSessionData,
            }[tab];
            setData({ sessions: [], fbp_allocation: 0, total_count: 0 });
        } finally {
            setLoading(false);
        }
    }, [empId, sort, sortDirection, limit, grade, filterParams, searchTerm]);

    useEffect(() => {
        if (grade || activeTab === "manageSessions") {
            const tabMap = {
                upcomingSessions: "upcoming",
                sessionHistory: "history",
                manageSessions: "manage",
            };
            fetchSessions(tabMap[activeTab], filterParams, searchTerm, sort, sortDirection, offset, limit, offset > 0);
        }
    }, [activeTab, grade, searchTerm, filterParams, sort, sortDirection, offset, fetchSessions]);

    const fetchCertificationCount = async () => {
        try {
            const params = { emp_id: empId };
            const statsData = await getCertificationCount(params);
            setFbpAllocation(statsData.fbp_allocation);
            setGrade(statsData.grade); // Set the grade
            sets3Link(statsData.s3Link)
        } catch (error) {
            console.error("Error fetching certification count:", error);
            setError("Error fetching certification count");
        }
    };

    const applyFilters = () => {
        setFilterParams(tempFilterParams); // Apply temporary filter values
        setOffset(0);
        fetchSessions('history', tempFilterParams, searchTerm, sort, sortDirection, 0, limit, false);
    };

    // seq 9.21 - 9.22
    // Function to handle button click and open signed URL
    const handleS3LinkClick = async (s3Link) => {
        try {
            // Call getS3url to get the signed URL
            const signedUrlResponse = await getS3(s3Link);

            // Adjust the property name based on your actual response structure
            const signedUrl = signedUrlResponse?.signedUrl || signedUrlResponse;

            if (signedUrl) {
                // Open the signed URL in a new tab
                window.open(signedUrl, '_blank');
            } else {
                console.error('No signed URL received in response');
            }
        } catch (error) {
            console.error('Error fetching signed S3 URL:', error);
        }
    };

    const clearFilters = () => {
        setTempFilterParams({ startDate: '', endDate: '', status: '' }); // Reset temporary filters
        setFilterParams({ startDate: '', endDate: '', status: '' }); // Reset actual filters
        setSearchTerm('');
        setSort('session_name');
        setSortDirection('asc');
        setOffset(0);
        fetchSessions('history', { startDate: '', endDate: '', status: '' }, '', 'session_name', 'asc', 0, limit, false);
    };

    const handleSort = (field) => {
        if (sort === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSort(field);
            setSortDirection('asc');
        }
        setOffset(0);
    };

    const handleShowMore = () => {
        setOffset(prev => prev + limit);
    };

    const isFilterApplied = Object.values(tempFilterParams).some(value => value !== '') || searchTerm !== '';

    return (
        <div className="body-bg">
            <div className="container-fluid mt-6">
                <Toaster />
                <div className="row justify-content-center">

                    <div className="col-md-12 d-flex align-items-center">
                        <BreadCrumbs data={breadCrumnData} />
                    </div>
                    <div>
                        <ul className="nav nav-pills" id="pills-tab" role="tablist">
                            <li className="nav-item">
                                <button
                                    className={`nav-link rounded-0 font-12 bold-text tab-style me-4 ${activeTab === 'upcomingSessions' ? 'active' : ''}`}
                                    id="upcomingSessionsTab"
                                    data-bs-toggle="pill"
                                    data-bs-target="#upcomingSessions"
                                    type="button"
                                    role="tab"
                                    aria-controls="upcomingSessions"
                                    aria-selected={activeTab === 'upcomingSessions'}
                                    ref={(el) => (tabRefs.current.upcomingSessions = el)}
                                    onClick={() => setActiveTab('upcomingSessions')}
                                >
                                    Upcoming Sessions
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link rounded-0 font-12 bold-text tab-style me-4 ${activeTab === 'sessionHistory' ? 'active' : ''}`}
                                    id="sessionHistoryTab"
                                    data-bs-toggle="pill"
                                    data-bs-target="#sessionHistory"
                                    type="button"
                                    role="tab"
                                    aria-controls="sessionHistory"
                                    aria-selected={activeTab === 'sessionHistory'}
                                    ref={(el) => (tabRefs.current.sessionHistory = el)}
                                    onClick={() => setActiveTab('sessionHistory')}
                                >
                                    {groupId == 1 ? 'My Session History' : 'Session History'}
                                </button>
                            </li>
                            {groupId == 1 && (
                                <li className="nav-item">
                                    <button
                                        className={`nav-link rounded-0 font-12 bold-text tab-style me-4 ${activeTab === 'manageSessions' ? 'active' : ''}`}
                                        id="manageSessionsTab"
                                        data-bs-toggle="pill"
                                        data-bs-target="#manageSessions"
                                        type="button"
                                        role="tab"
                                        aria-controls="manageSessions"
                                        aria-selected={activeTab === 'manageSessions'}
                                        ref={(el) => (tabRefs.current.manageSessions = el)}
                                        onClick={() => setActiveTab('manageSessions')}
                                    >
                                        Manage Sessions
                                    </button>
                                </li>
                            )}
                            {/* Search and Filter Section */}
                            {/* Search and Filter Section */}
                            {activeTab === 'sessionHistory' && (
                                <div className="ms-auto d-flex">
                                    <div className="input-group flex-nowrap search-group me-3">
                                        <input
                                            type="search"
                                            className="form-control search-align"
                                            placeholder="Search"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onBlur={() => fetchSessions('history', filterParams, searchTerm, sort, sortDirection, offset, limit, false)}
                                            onKeyPress={(e) => e.key === 'Enter' && fetchSessions('history', filterParams, searchTerm, sort, sortDirection, offset, limit, false)}
                                            aria-label="Search"
                                        />
                                        <button className="input-group-text btn btn-search-custom" onClick={() => fetchSessions('history', filterParams, searchTerm, sort, sortDirection, offset, limit, false)}>
                                            <img src="Images/search-icon.svg" alt="search-icon" />
                                        </button>
                                    </div>
                                    {/* Filter Section Starts Here */}
                                    <div className="dropdown">
                                        <button
                                            className="advanced-filter-button me2 medium-text"
                                            type="button"
                                            id="dropdownMenuButton1"
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                        >
                                            <img src="/Images/filter-icon.svg" alt="Filter-icon" className="me-2" />Filter
                                        </button>

                                        <div className="dropdown-menu px-3 advanced-filter-panel shadow session-filter mt-5" aria-labelledby="dropdownMenuButton1">
                                            <div className="row">
                                                <div className="col-md-12 py-2 border-0 mb-1">
                                                    <span className="font-16 semibold-text advanced-filter-title">Advanced Filter</span>
                                                    <button type="button" className="btn-close float-end close-filter" data-bs-dismiss="advanced-filter" aria-label="Close"></button>
                                                </div>

                                                <div className="col-md-12 pb-2 border-0 ">
                                                    <span className="font-14 semibold-text filter-title">Session Date</span>
                                                </div>

                                                <div className="row mb-3">
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label hive-form-label" htmlFor="fromDate">From</label>
                                                        <input
                                                            type="date"
                                                            className="form-control hive-form-control"
                                                            id="fromDate"
                                                            value={tempFilterParams.startDate || ""}
                                                            onChange={(e) => {
                                                                const newStartDate = e.target.value;
                                                                setTempFilterParams((prev) => ({
                                                                    ...prev,
                                                                    startDate: newStartDate,
                                                                    endDate: prev.endDate && prev.endDate < newStartDate ? "" : prev.endDate,
                                                                }));
                                                            }}
                                                        />
                                                    </div>

                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label hive-form-label" htmlFor="ToDate">To</label>
                                                        <input
                                                            type="date"
                                                            className="form-control hive-form-control"
                                                            id="ToDate"
                                                            min={tempFilterParams.startDate || ""}
                                                            value={tempFilterParams.endDate || ""}
                                                            onChange={(e) =>
                                                                setTempFilterParams({ ...tempFilterParams, endDate: e.target.value })
                                                            }
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label hive-form-label" htmlFor="status">Status</label>
                                                    <select
                                                        className="form-select hive-form-select"
                                                        aria-label="Default select example2"
                                                        id="status"
                                                        value={tempFilterParams.status}
                                                        onChange={(e) => setTempFilterParams({ ...tempFilterParams, status: e.target.value })}
                                                    >
                                                        <option selected="" value="">Select</option>
                                                        <option value="Register">Registered</option>
                                                        <option value="Reschedule">Rescheduled</option>
                                                        <option value="Complete">Completed</option>
                                                    </select>
                                                </div>

                                                <div className="col-md-12 my-3 d-flex justify-content-end">
                                                    <button className="btn hive-btn-secondary me-3" onClick={clearFilters}>Clear</button>
                                                    <button className="btn hive-btn-primary" onClick={applyFilters} disabled={!isFilterApplied}>Apply</button>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                    {/* Filter Section Ends Here */}
                                    <div class="ms-auto d-flex" >
                                        <button class="btn hive-btn-outline more-indicator-1 float-left cursor-pointer ms-2"
                                            onClick={() => handleS3LinkClick(s3Link)}>
                                            <img src="/Images/manual.svg" class="me-2" alt="import-icon" />
                                        </button>
                                        <div class="dropdown-items-1 border border-light mt-2 bg-white rounded-3 p-2 shadow">
                                            <div class="d-flex flex-wrap gap-2">
                                                <span class="practices-badge px-2 py-1 rounded-2">Click here to download the user manual</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {activeTab === 'manageSessions' && manageSessionData.sessions.length > 0 && ( // Check if sessionData has items
                                <div className="ms-auto d-flex">
                                    <button className="btn hive-btn-primary small-btn text-nowrap" onClick={() => setShowModal(true)}>
                                        <img src="Images/hive-add-icon.svg" className="me-2" alt="add-icon" />Create Session
                                    </button>
                                    <button class="btn hive-btn-outline more-indicator-1 float-left cursor-pointer ms-2"
                                        onClick={() => handleS3LinkClick(s3Link)}>
                                        <img src="/Images/manual.svg" class="me-2" alt="import-icon" />
                                    </button>
                                    <div class="dropdown-items-1 border border-light mt-2 bg-white rounded-3 p-2 shadow">
                                        <div class="d-flex flex-wrap gap-2">
                                            <span class="practices-badge px-2 py-1 rounded-2">Click here to download the user manual</span>
                                        </div>
                                    </div>
                                </div>

                            )}

                            {activeTab !== 'sessionHistory' && activeTab !== 'manageSessions' && <div class="ms-auto d-flex" >
                                <button class="btn hive-btn-outline more-indicator-1 float-left cursor-pointer ms-2"
                                    onClick={() => handleS3LinkClick(s3Link)}>
                                    <img src="/Images/manual.svg" class="me-2" alt="import-icon" />
                                </button>
                                <div class="dropdown-items-1 border border-light mt-2 bg-white rounded-3 p-2 shadow">
                                    <div class="d-flex flex-wrap gap-2">
                                        <span class="practices-badge px-2 py-1 rounded-2">Click here to download the user manual</span>
                                    </div>
                                </div>
                            </div>}

                        </ul>
                    </div>
                </div>

                {/* Session List Tab Content */}
                <div className="tab-content mt-4" id="pills-tabContent">
                    <div className={`tab-pane fade show ${activeTab === 'upcomingSessions' ? 'active' : ''}`} id="upcomingSessions" role="tabpanel" aria-labelledby="upcomingSessionsTab">
                        {/* Upcoming Sessions Content */}
                        <UpcomingSession
                            sessionData={upcomingSessionData.sessions}
                            fbpAllocation={fbpAllocation}
                            fetchSessions={(...args) => fetchSessions('upcoming', ...args)}
                        />
                    </div>
                    <div className={`tab-pane fade ${activeTab === 'sessionHistory' ? 'show active' : ''}`} id="sessionHistory" role="tabpanel" aria-labelledby="sessionHistoryTab">
                        <SessionHistory
                            sessionData={historySessionData.sessions}
                            totalCount={totalCount}
                            onSort={handleSort}
                            onShowMore={handleShowMore}
                            loading={loading}
                            sort={sort}
                            sortDirection={sortDirection}
                        />
                    </div>
                    {groupId == 1 && (
                        <div className={`tab-pane fade ${activeTab === 'manageSessions' ? 'show active' : ''}`} id="manageSessions" role="tabpanel" aria-labelledby="manageSessionsTab">
                            {/* Manage Sessions Content */}
                            <ManageSession
                                sessionData={manageSessionData.sessions}
                                fetchSessions={(...args) => fetchSessions('manage', ...args)}
                            />
                        </div>
                    )}
                </div>
            </div >

            {/* Render the SessionForm modal */}
            {
                showModal && (
                    <SessionForm
                        onClose={() => setShowModal(false)}
                        fetchSessions={(...args) => fetchSessions('manage', ...args)}
                    /> // Pass close handler
                )
            }
        </div >
    );
};
